import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { validateAll, runRules } from '../utils/validators';
import { trackEvent } from '../api/tracker';

/**
 * One hook behind every form on the site.
 *
 * Replaces the per-page copy of this logic, which had three problems:
 *  - the debounce helper was rebuilt on each render, so it never deduped
 *  - `required` attributes did nothing because submit ran off a button onClick
 *  - one toast for the whole form, instead of a message beside the bad field
 *
 * @param {object}   options
 * @param {object}   options.initialValues
 * @param {object}   options.schema        { field: [rule, rule] } from utils/validators
 * @param {Function} options.onSubmit      async (values) => any; may throw
 * @param {string}   options.formName      used for analytics events
 * @param {string}   options.page          page name for analytics
 * @param {string}   options.successMessage
 * @param {boolean}  options.resetOnSuccess
 */
export default function useForm({
  initialValues = {},
  schema = {},
  onSubmit,
  formName = 'form',
  page,
  successMessage = 'Sent. We will be in touch shortly.',
  resetOnSuccess = true,
  scrollToTopOnSuccess = false,
} = {}) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // A ref, not state: it blocks the second call synchronously, before React has
  // a chance to re-render. This is what actually stops double submissions.
  const inFlight = useRef(false);
  const startedTracking = useRef(false);
  const firstErrorRef = useRef(null);

  /** Fires 'form_start' once, the first time the visitor types. */
  const markStarted = useCallback(() => {
    if (startedTracking.current) return;
    startedTracking.current = true;
    trackEvent('form_start', { label: formName, page, category: 'form' });
  }, [formName, page]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked, files } = e.target;
    const next = type === 'checkbox' ? checked : type === 'file' ? files[0] : value;

    markStarted();
    setValues((prev) => ({ ...prev, [name]: next }));

    // Clear the error as soon as they start fixing it, but do not show a new
    // one mid-typing — that is what blur is for.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
    setIsSuccess(false);
  }, [markStarted]);

  /** For values that do not come from a DOM event (chips, pickers). */
  const setValue = useCallback((name, value) => {
    markStarted();
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
  }, [markStarted]);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const rules = schema[name];
    if (!rules) return;
    setValues((current) => {
      const message = runRules(current[name], rules);
      setErrors((prev) => ({ ...prev, [name]: message }));
      return current;
    });
  }, [schema]);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSuccess(false);
    startedTracking.current = false;
  }, [initialValues]);

  /**
   * Wired to <form onSubmit>, so Enter works and the browser's own semantics
   * apply. The submit button stays type="submit".
   */
  const handleSubmit = useCallback(async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (inFlight.current) return;

    const validationErrors = validateAll(values, schema);
    const badFields = Object.keys(validationErrors);

    if (badFields.length) {
      setErrors(validationErrors);
      setTouched(Object.fromEntries(badFields.map((f) => [f, true])));

      trackEvent('form_error', {
        label: formName, page, category: 'form',
        meta: { fields: badFields },
      });

      toast.error(
        badFields.length === 1
          ? validationErrors[badFields[0]]
          : `Check ${badFields.length} fields highlighted below.`
      );

      // Put the cursor where the problem is.
      const el = document.querySelector(`[name="${badFields[0]}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
      firstErrorRef.current = badFields[0];
      return;
    }

    inFlight.current = true;
    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await onSubmit(values);

      setIsSuccess(true);
      if (resetOnSuccess) reset();
      if (scrollToTopOnSuccess) window.scrollTo({ top: 0, behavior: 'smooth' });

      toast.success(result?.message || successMessage);
      return result;
    } catch (error) {
      // The API returns { fields: { email: '...' } } for field-level problems.
      if (error.fields && Object.keys(error.fields).length) {
        setErrors(error.fields);
        setTouched((prev) => ({
          ...prev,
          ...Object.fromEntries(Object.keys(error.fields).map((f) => [f, true])),
        }));
      }

      trackEvent('form_error', {
        label: formName, page, category: 'form',
        meta: { server: true, status: error.status },
      });

      toast.error(error.message || 'We could not send that. Please try again.');
      return null;
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  }, [values, schema, onSubmit, formName, page, successMessage, resetOnSuccess, reset, scrollToTopOnSuccess]);

  /** Spread onto an input to wire value, handlers and accessible error state. */
  const fieldProps = useCallback((fieldName) => ({
    name: fieldName,
    value: values[fieldName] ?? '',
    onChange: handleChange,
    onBlur: handleBlur,
    'aria-invalid': Boolean(errors[fieldName]) || undefined,
    'aria-describedby': errors[fieldName] ? `${fieldName}-error` : undefined,
  }), [values, errors, handleChange, handleBlur]);

  return {
    values, errors, touched, isSubmitting, isSuccess,
    handleChange, handleBlur, handleSubmit, setValue, setValues, reset, fieldProps,
    hasErrors: Object.values(errors).some(Boolean),
  };
}
